import React from 'react';
import { useTranslation } from 'react-i18next';
import { Order, VehicleTypes, OrderDestination } from '@hero/schema';
import { VehicleName } from '../../Vehicle/VehicleName';
import { useOrderDestination } from '@hero/hooks-shared';
import { SavedLocationIcon, StarIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../ui/Tooltip';
import { ClientDisplay } from '../../Client/ClientDisplay';
import { RegionName } from '../../../app/RegionName/RegionName';
import { InaccurateLocationHint } from './InaccurateLocationHint';

function useDir() {
  const { i18n } = useTranslation();
  return i18n.dir();
}

/**
 * Displays location information (city/country or saved location name)
 */
interface LocationDisplayProps {
  country: string;
  city: string;
  cityCustomCode?: string;
  neighborhood?: string;
  destination?: OrderDestination;
  onSaveLocation?: () => void;
  align?: 'start' | 'end';
  /** Hide the amber inaccurate-pin icon next to the city name. */
  hideUpdateLocation?: boolean;
}

const LocationCore = ({
  country,
  city,
  cityCustomCode,
  neighborhood,
  destination,
  onSaveLocation,
  align = 'start',
  hideUpdateLocation,
}: LocationDisplayProps) => {
  const savedLocationName = destination?.savedPlace?.name;
  const showSaveAction = !savedLocationName && onSaveLocation;
  // Same rule as the table route column: only show when explicitly inaccurate.
  const showInaccurate =
    !hideUpdateLocation && destination?.isAccurateLocation === false;
  // Origin (align end): `[i] City` — Destination (align start): `City [i]`
  const iconBefore = showInaccurate && align === 'end';
  const iconAfter = showInaccurate && align === 'start';

  const subtitleText = savedLocationName || neighborhood || country;

  return (
    <div
      className={cn(
        'flex flex-col justify-center flex-1 min-w-0',
        align === 'end' ? 'items-end' : 'items-start',
      )}>
      <div
        className={cn(
          'flex items-center gap-1 min-w-0 max-w-full',
          align === 'end' ? 'ms-auto' : 'me-auto',
        )}>
        {savedLocationName ? (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-medium text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded truncate"
            title={savedLocationName}>
            <SavedLocationIcon className="size-2.5 flex-shrink-0 text-teal-600" />
            {savedLocationName}
          </span>
        ) : (
          <>
            <span className="text-gray-400 caption truncate" title={subtitleText}>
              {subtitleText}
            </span>
            {showSaveAction && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onSaveLocation?.();
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                title="Save location">
                <StarIcon className="size-3" />
              </button>
            )}
          </>
        )}
      </div>
      {/* City + accuracy hint: `[i] Origin` / `Destination [i]` */}
      <div
        className={cn(
          'flex items-center gap-0.5 min-w-0 max-w-full',
          align === 'end' ? 'ms-auto' : 'me-auto',
        )}>
        {iconBefore ? <InaccurateLocationHint /> : null}
        <span className="text-black subtitle2 truncate" title={city}>
          {cityCustomCode ? (
            <RegionName customCode={cityCustomCode} fallback={city} />
          ) : (
            city
          )}
        </span>
        {iconAfter ? <InaccurateLocationHint /> : null}
      </div>
    </div>
  );
};

/**
 * Collects client / beneficiary badges centered below the route.
 */
const RouteBadges: React.FC<{
  destinations: (OrderDestination | undefined)[];
  /** CRM client on the order (`Order.client.name`). Preferred over per-destination beneficiary chips. */
  clientName?: string;
  hideCustomerName?: boolean;
  /** Opens assign/change client when the carrier can manage the order. */
  onClientClick?: () => void;
  /** Label when there is no client yet but editing is allowed. */
  addClientLabel?: string;
}> = ({ destinations, clientName, hideCustomerName, onClientClick, addClientLabel }) => {
  const badges: React.ReactNode[] = [];

  if (!hideCustomerName) {
    const trimmedClientName = clientName?.trim();
    if (trimmedClientName) {
      badges.push(
        <TooltipProvider key={`client-${trimmedClientName}`} delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <ClientDisplay
                clientName={trimmedClientName}
                className="text-[10px] py-0.5 px-1.5 cursor-default"
                onClick={onClientClick}
              />
            </TooltipTrigger>
            <TooltipContent side="top">{trimmedClientName}</TooltipContent>
          </Tooltip>
        </TooltipProvider>,
      );
    } else if (onClientClick && addClientLabel) {
      badges.push(
        <button
          key="add-client"
          type="button"
          onClick={e => {
            e.stopPropagation();
            onClientClick();
          }}
          className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-600 hover:text-blue-800 px-1.5 py-0.5 rounded-md border border-dashed border-blue-300 hover:border-blue-400 bg-blue-50/50">
          {addClientLabel}
        </button>,
      );
    } else {
      // Fallback for legacy orders without `Order.client` — de-duplicate
      // beneficiary chips from pickup/dropoff destinations.
      const seen = new Set<string>();
      destinations.forEach(dest => {
        const name = dest?.beneficiary?.name?.trim();
        if (!name || seen.has(name)) return;
        seen.add(name);
        badges.push(
          <TooltipProvider key={`ben-${dest!.id}-${name}`} delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <ClientDisplay
                  clientName={name}
                  className="text-[10px] py-0.5 px-1.5 cursor-default"
                />
              </TooltipTrigger>
              <TooltipContent side="top">{name}</TooltipContent>
            </Tooltip>
          </TooltipProvider>,
        );
      });
    }
  }

  if (badges.length === 0) return null;

  return <div className="flex items-center justify-center gap-1.5 w-full">{badges}</div>;
};

/**
 * Displays vehicle information with visual route indicator
 */
interface VehicleDisplayProps {
  vehicle: VehicleTypes | undefined;
}

const VehicleDisplay = ({ vehicle }: VehicleDisplayProps) => {
  if (!vehicle) {
    return null;
  }

  return (
    <div className="flex flex-col items-center justify-center flex-[2] min-w-0">
      <VehicleName vehicle={vehicle} className="text-black caption whitespace-nowrap" />
      <div className="flex items-center gap-1 w-full">
        <div className="border border-primary-500 w-1.5 h-1.5 rounded-full flex-shrink-0" />
        <div className="border-dashed border flex-1 min-w-5 border-[rgba(52,83,217,0.24)]" />
        <img
          src="/images/publishOrder/truck.svg"
          alt={vehicle}
          className="flex-shrink-0 rtl:scale-x-[-1]"
        />
        <div className="border-dashed border flex-1 min-w-5 border-[rgba(52,83,217,0.24)]" />
        <div className="bg-primary-500 w-1.5 h-1.5 rounded-full flex-shrink-0" />
      </div>
    </div>
  );
};

/**
 * Displays the route information with origin, vehicle and destination
 */
export interface OrderRouteDisplayProps {
  destinations: Order['destinations'];
  vehicle: VehicleTypes | undefined;
  className?: string;
  order?: Order; // Optional - for click handler and `Order.client` badge
  onClick?: (order: Order) => void; // Optional - click handler to open map
  onSaveLocation?: (destination: OrderDestination) => void; // Optional - save location handler
  /** Override CRM client label under the route. Defaults to `order.client?.name`. */
  clientName?: string;
  hideCustomerName?: boolean; // Hide client / beneficiary name badges
  hideUpdateLocation?: boolean; // Hide inaccurate location badges
  /** Opens assign/change client modal when the viewer can manage the order. */
  onClientClick?: () => void;
  /** Label for the empty-client affordance (requires `onClientClick`). */
  addClientLabel?: string;
}

export const OrderRouteDisplay: React.FC<OrderRouteDisplayProps> = ({
  destinations,
  vehicle,
  className = '',
  order,
  onClick,
  onSaveLocation,
  clientName,
  hideCustomerName,
  hideUpdateLocation,
  onClientClick,
  addClientLabel,
}) => {
  const dir = useDir();
  const {
    firstPickupDisplay,
    lastDropoffDisplay,
    getDisplayDestination,
    firstPickup,
    lastDropoff,
  } = useOrderDestination(destinations);

  const resolvedClientName = clientName ?? order?.client?.name;

  const handleClick = (e: React.MouseEvent) => {
    if (onClick && order) {
      e.stopPropagation();
      onClick(order);
    }
  };

  const isClickable = !!onClick && !!order;

  return (
    <div
      dir={dir}
      className={cn(
        'flex flex-col gap-1.5 rounded-lg px-3 py-2.5 transition-colors duration-150',
        className,
        isClickable && 'cursor-pointer hover:bg-blue-50/60 active:bg-blue-50',
      )}
      onClick={isClickable ? handleClick : undefined}>
      {/* Route row: Pickup → Vehicle → Dropoff */}
      <div className="flex items-center justify-center gap-2 w-full">
        <LocationCore
          country={firstPickupDisplay.country || ''}
          city={firstPickupDisplay.city || ''}
          cityCustomCode={firstPickup?.address?.city?.customCode}
          neighborhood={firstPickupDisplay.neighborhood || ''}
          destination={firstPickup}
          align="end"
          hideUpdateLocation={hideUpdateLocation}
          onSaveLocation={
            onSaveLocation && firstPickup ? () => onSaveLocation(firstPickup) : undefined
          }
        />
        <VehicleDisplay vehicle={vehicle} />
        <LocationCore
          country={lastDropoffDisplay.country || ''}
          city={lastDropoffDisplay.city || ''}
          cityCustomCode={lastDropoff?.address?.city?.customCode}
          neighborhood={lastDropoffDisplay.neighborhood || ''}
          destination={lastDropoff}
          align="start"
          hideUpdateLocation={hideUpdateLocation}
          onSaveLocation={
            onSaveLocation && lastDropoff ? () => onSaveLocation(lastDropoff) : undefined
          }
        />
      </div>
      {/* Badges row: client / beneficiary chips centered below the route */}
      <RouteBadges
        destinations={[firstPickup, lastDropoff]}
        clientName={resolvedClientName}
        hideCustomerName={hideCustomerName}
        onClientClick={onClientClick}
        addClientLabel={addClientLabel}
      />
    </div>
  );
};
